import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TablePagination, TextField, Box, Checkbox, FormControlLabel,
    Popover, Button, CircularProgress, Typography, IconButton,
    List, ListItem, ListItemText, ListItemIcon, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';

const DataTable = ({
    data,
    columns,
    headerStyles = {},
    total,
    page,
    limit,
    onPageChange,
    onLimitChange,
    onSort,
    onFilter,
    loading,
    selectedRows = [],
    onSelectionChange,
    isGlobalSelection = false,
    onGlobalSelectionChange,
    customRenderers = {},
    filterTypes = {}, // { column: 'select' | 'text' | { type: 'select', options: [] } }
    onFetchUniqueValues, // (column) => Promise<Array<string>>
    onColumnVisibilityChange,
    renderHeaderActions
}) => {
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [activeFilterCol, setActiveFilterCol] = useState(null);
    const [uniqueValues, setUniqueValues] = useState({});
    const [loadingValues, setLoadingValues] = useState(false);
    const [filters, setFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    useEffect(() => {
        if (columns.length > 0) {
            setVisibleColumns(columns);
            if (onColumnVisibilityChange) {
                onColumnVisibilityChange(columns);
            }
        }
    }, [columns, onColumnVisibilityChange]);

    const handleToggleAll = (event) => {
        if (!onSelectionChange) return;

        if (event.target.checked) {
            const allIds = data.map(row => row.id).filter(id => id !== undefined);
            onSelectionChange(allIds);
            if (onGlobalSelectionChange) onGlobalSelectionChange(true);
        } else {
            onSelectionChange([]);
            if (onGlobalSelectionChange) onGlobalSelectionChange(false);
        }
    };

    const handleToggleRow = (id) => {
        if (!onSelectionChange) return;

        let newSelection;
        if (isGlobalSelection) {
            // If globally selected, unchecking one row drops global selection
            // and selects everything on the page EXCEPT this one.
            const allIdsOnPage = data.map(row => row.id);
            newSelection = allIdsOnPage.filter(rowId => rowId !== id);
            if (onGlobalSelectionChange) onGlobalSelectionChange(false);
        } else {
            newSelection = selectedRows.includes(id)
                ? selectedRows.filter(rowId => rowId !== id)
                : [...selectedRows, id];
        }
        onSelectionChange(newSelection);
    };

    const isAllSelected = isGlobalSelection || (data.length > 0 && data.every(row => selectedRows.includes(row.id)));
    const isSomeSelected = !isGlobalSelection && data.some(row => selectedRows.includes(row.id)) && !isAllSelected;


    const handleSort = (column) => {
        let direction = 'asc';
        if (sortConfig.key === column && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: column, direction });
        onSort(column, direction);
    };

    const handleFilterChange = (column, value) => {
        const newFilters = { ...filters, [column]: value };
        // Remove empty filters - be careful not to remove 0
        if ((value === undefined || value === null || value === '') || (Array.isArray(value) && value.length === 0)) {
            delete newFilters[column];
        }
        setFilters(newFilters);
        onFilter(newFilters);
    };

    const handleClearFilters = () => {
        setFilters({});
        onFilter({});
    };

    const handleColumnToggle = (column) => {
        let newVisibleColumns;
        if (visibleColumns.includes(column)) {
            newVisibleColumns = visibleColumns.filter(c => c !== column);
        } else {
            newVisibleColumns = [...visibleColumns, column];
        }
        setVisibleColumns(newVisibleColumns);
        if (onColumnVisibilityChange) {
            onColumnVisibilityChange(newVisibleColumns);
        }
    };

    const handleSelectAllColumns = () => {
        setVisibleColumns(columns);
        if (onColumnVisibilityChange) {
            onColumnVisibilityChange(columns);
        }
    };

    const handleUnselectAllColumns = () => {
        setVisibleColumns([]);
        if (onColumnVisibilityChange) {
            onColumnVisibilityChange([]);
        }
    };

    const openColumnSelector = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const closeColumnSelector = () => {
        setAnchorEl(null);
    };

    const handleOpenFilterPopup = async (event, column) => {
        setFilterAnchorEl(event.currentTarget);
        setActiveFilterCol(column);

        const filterConfig = filterTypes[column];
        const isSelect = filterConfig === 'select' || (filterConfig && filterConfig.type === 'select');
        const staticOptions = filterConfig && filterConfig.options;

        if (isSelect) {
            if (staticOptions) {
                setUniqueValues(prev => ({ ...prev, [column]: staticOptions }));
            } else if (!uniqueValues[column] && onFetchUniqueValues) {
                setLoadingValues(true);
                try {
                    const values = await onFetchUniqueValues(column);
                    setUniqueValues(prev => ({ ...prev, [column]: values }));
                } catch (err) {
                    console.error("Failed to fetch unique values:", err);
                } finally {
                    setLoadingValues(false);
                }
            }
        }
    };

    const handleCloseFilterPopup = () => {
        setFilterAnchorEl(null);
        setActiveFilterCol(null);
    };

    const handleToggleFilterValue = (column, value) => {
        const currentSelected = Array.isArray(filters[column]) ? filters[column] : [];
        let newSelected;
        if (currentSelected.includes(value)) {
            newSelected = currentSelected.filter(v => v !== value);
        } else {
            newSelected = [...currentSelected, value];
        }

        const newFilters = { ...filters, [column]: newSelected };
        if (newSelected.length === 0) {
            delete newFilters[column];
        }
        setFilters(newFilters);
        onFilter(newFilters);
    };

    // Helper for compact date formatting
    const formatDate = (val) => {
        if (!val || typeof val !== 'string') return val;
        // Detect long date format: "Thu Sep 19 2024 02:00:00 GMT+0200 (heure d'été d'Europe centrale)"
        if (val.length > 20 && (val.includes('GMT') || val.includes('UTC'))) {
            try {
                const date = new Date(val);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                }
            } catch (e) {
                return val;
            }
        }
        return val;
    };

    const open = Boolean(anchorEl);

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid #eee', alignItems: 'center', gap: 2 }}>
                {isGlobalSelection && (
                    <Box sx={{ flexGrow: 1, backgroundColor: '#e3f2fd', p: 1, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2">
                            Tous les <strong>{total}</strong> fournisseurs sont sélectionnés. {' '}
                            <Button size="small" onClick={() => {
                                if (onGlobalSelectionChange) onGlobalSelectionChange(false);
                                if (onSelectionChange) onSelectionChange([]);
                            }} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
                                Annuler la sélection
                            </Button>
                        </Typography>
                    </Box>
                )}
                {Object.keys(filters).length > 0 && (
                    <Button
                        startIcon={<FilterListOffIcon />}
                        onClick={handleClearFilters}
                        variant="outlined"
                        size="small"
                        color="error"
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Disable Filters
                    </Button>
                )}
                {renderHeaderActions && renderHeaderActions()}
                <Button
                    startIcon={<ViewColumnIcon />}
                    onClick={openColumnSelector}
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                    View Columns
                </Button>
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={closeColumnSelector}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: { borderRadius: 2, mt: 0.5, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }
                    }}
                >
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', maxHeight: 400, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderBottom: '1px solid #eee', pb: 1 }}>
                            <Button
                                size="small"
                                onClick={handleSelectAllColumns}
                                sx={{ textTransform: 'none', fontWeight: 'bold' }}
                            >
                                Select All
                            </Button>
                            <Button
                                size="small"
                                onClick={handleUnselectAllColumns}
                                sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                color="error"
                            >
                                Unselect All
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                            {columns.map((col) => (
                                <FormControlLabel
                                    key={col}
                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={visibleColumns.includes(col)}
                                            onChange={() => handleColumnToggle(col)}
                                        />
                                    }
                                    label={headerStyles[col]?.label || col}
                                />
                            ))}
                        </Box>
                    </Box>
                </Popover>
            </Box>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader aria-label="compact table" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                padding="checkbox"
                                sx={{
                                    backgroundColor: '#f8f9fa !important',
                                    borderBottom: '2px solid #ddd',
                                    width: 48,
                                    py: 1,
                                    px: 1
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    indeterminate={isSomeSelected}
                                    checked={isAllSelected}
                                    onChange={handleToggleAll}
                                />
                            </TableCell>
                            {visibleColumns.map((column) => (
                                <TableCell
                                    key={column}
                                    sx={{
                                        minWidth: 120,
                                        backgroundColor: headerStyles[column]?.color ? `${headerStyles[column].color} !important` : '#f8f9fa !important',
                                        color: headerStyles[column]?.color ? '#000' : '#555',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        borderBottom: '2px solid #ddd',
                                        py: 1,
                                        px: 1,
                                        '&:hover': {
                                            backgroundColor: headerStyles[column]?.color ? `${headerStyles[column].color}` : '#f0f1f2 !important',
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box
                                            onClick={() => column !== 'Actions' && handleSort(column)}
                                            sx={{
                                                cursor: column === 'Actions' ? 'default' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                pr: 0.5
                                            }}
                                        >
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {headerStyles[column]?.label || column}
                                            </span>
                                            {sortConfig.key === column && (
                                                <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                                    {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                                                </span>
                                            )}
                                        </Box>
                                        {column !== 'Actions' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {(filterTypes[column] === 'select' || (filterTypes[column] && filterTypes[column].type === 'select')) ? (
                                                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                                                        <Box
                                                            onClick={(e) => handleOpenFilterPopup(e, column)}
                                                            sx={{
                                                                flexGrow: 1,
                                                                fontSize: '0.7rem',
                                                                backgroundColor: 'rgba(255,255,255,0.5)',
                                                                borderRadius: '4px',
                                                                px: 0.5,
                                                                height: '24px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                cursor: 'pointer',
                                                                color: filters[column]?.length > 0 ? '#1976d2' : '#777',
                                                                fontWeight: filters[column]?.length > 0 ? 700 : 400,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {filters[column]?.length > 0
                                                                ? `${filters[column].length} selected`
                                                                : 'All values'}
                                                        </Box>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleOpenFilterPopup(e, column)}
                                                            sx={{ p: 0, ml: 0.5, color: filters[column]?.length > 0 ? '#1976d2' : 'inherit' }}
                                                        >
                                                            <FilterAltIcon sx={{ fontSize: '1rem' }} />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <TextField
                                                        size="small"
                                                        placeholder="Filter..."
                                                        variant="standard"
                                                        fullWidth
                                                        value={filters[column] || ''}
                                                        onChange={(e) => handleFilterChange(column, e.target.value)}
                                                        InputProps={{
                                                            disableUnderline: true,
                                                            sx: {
                                                                fontSize: '0.7rem',
                                                                backgroundColor: 'rgba(255,255,255,0.5)',
                                                                borderRadius: '4px',
                                                                px: 0.5,
                                                                height: '20px',
                                                                '&:hover': { backgroundColor: 'white' },
                                                                '&.Mui-focused': { backgroundColor: 'white', border: '1px solid #ccc' }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                            <TableCell
                                padding="checkbox"
                                sx={{
                                    backgroundColor: '#f8f9fa !important',
                                    borderBottom: '2px solid #ddd',
                                    width: 48,
                                    py: 1,
                                    px: 1
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    indeterminate={isSomeSelected}
                                    checked={isAllSelected}
                                    onChange={handleToggleAll}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={30} />
                                </TableCell>
                            </TableRow>
                        ) : data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="textSecondary">No records to display</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row, index) => (
                                <TableRow
                                    hover
                                    role="checkbox"
                                    aria-checked={selectedRows.includes(row.id)}
                                    tabIndex={-1}
                                    key={row.id || index}
                                    selected={selectedRows.includes(row.id)}
                                    sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}
                                >
                                    <TableCell padding="checkbox" sx={{ borderRight: '1px solid #f0f0f0', px: 1 }}>
                                        <Checkbox
                                            size="small"
                                            checked={isGlobalSelection || selectedRows.includes(row.id)}
                                            onChange={() => handleToggleRow(row.id)}
                                        />
                                    </TableCell>
                                    {visibleColumns.map((column) => {
                                        const value = row[column];

                                        if (customRenderers[column]) {
                                            return (
                                                <TableCell
                                                    key={column}
                                                    sx={{
                                                        py: 0.5,
                                                        px: 1,
                                                        fontSize: '0.8125rem',
                                                        borderRight: '1px solid #f0f0f0'
                                                    }}
                                                >
                                                    {customRenderers[column](value, row)}
                                                </TableCell>
                                            );
                                        }
                                        return (
                                            <TableCell
                                                key={column}
                                                sx={{
                                                    py: 0.5,
                                                    px: 1,
                                                    fontSize: '0.8125rem',
                                                    color: '#333',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: 300,
                                                    borderRight: '1px solid #f0f0f0'
                                                }}
                                            >
                                                {formatDate(value)}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell
                                        padding="checkbox"
                                        sx={{
                                            borderLeft: '1px solid #f0f0f0',
                                            px: 1,
                                            backgroundColor: (isGlobalSelection || selectedRows.includes(row.id)) ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                                        }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isGlobalSelection || selectedRows.includes(row.id)}
                                            onChange={() => handleToggleRow(row.id)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 100, 250]}
                component="div"
                count={total}
                rowsPerPage={limit}
                page={page - 1} // MUI uses 0-based index
                onPageChange={(e, newPage) => onPageChange(newPage + 1)}
                onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
            />

            {/* Filter Popover for select types */}
            <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={handleCloseFilterPopup}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: { borderRadius: 2, minWidth: 200, maxWidth: 350, maxHeight: 400, mt: 0.5, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }
                }}
            >
                <Box sx={{ p: 1 }}>
                    <Typography variant="overline" sx={{ px: 1, fontWeight: 'bold', color: '#666' }}>
                        Filter {headerStyles[activeFilterCol]?.label || activeFilterCol}
                    </Typography>
                    {loadingValues ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, pb: 1, borderBottom: '1px solid #f0f0f0' }}>
                                <Button
                                    size="small"
                                    onClick={() => handleFilterChange(activeFilterCol, uniqueValues[activeFilterCol] || [])}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                    Select All
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => handleFilterChange(activeFilterCol, [])}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                    color="error"
                                >
                                    Unselect All
                                </Button>
                            </Box>
                            <List dense sx={{ py: 0, maxHeight: 250, overflowY: 'auto' }}>
                                {uniqueValues[activeFilterCol]?.length > 0 ? (
                                    uniqueValues[activeFilterCol].map((val) => (
                                        <ListItem
                                            key={val}
                                            button
                                            onClick={() => handleToggleFilterValue(activeFilterCol, val)}
                                            sx={{ borderRadius: 1 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox
                                                    edge="start"
                                                    checked={Array.isArray(filters[activeFilterCol]) && filters[activeFilterCol].includes(val)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    size="small"
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={val}
                                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                                            />
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>
                                        <ListItemText primary="No values found" />
                                    </ListItem>
                                )}
                            </List>
                        </>
                    )}
                    <Box sx={{ p: 1, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => handleFilterChange(activeFilterCol, [])}>Clear</Button>
                        <Button size="small" variant="contained" onClick={handleCloseFilterPopup} sx={{ ml: 1 }}>Apply</Button>
                    </Box>
                </Box>
            </Popover>
        </Paper>
    );
};

export default DataTable;
